import { useCallback, useEffect, useState } from 'react';
import { InfiniteData, MutationOptions, QueryKey, useInfiniteQuery, useMutation, UseMutationOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSetAtom } from 'jotai/react';
import { DTO_MESSAGE_INFO_MESSAGE_CONTENT_TYPES, type DTOMessageInfo } from 'src/apis/dto/DTOMessageInfo';
import { DTOMessageThreadInfo } from 'src/apis/dto/DTOMessageThreadInfo';
import { DTOOpenMessageThreadRequest } from 'src/apis/dto/DTOOpenMessageThreadRequest';
import type { DTOOpenMessageThreadResponsePaymentMethodType } from 'src/apis/dto/DTOOpenMessageThreadResponse';
import { REST_ERROR_CODES } from 'src/apis/exception';
import { JSONRPCClient, RESTClient } from 'src/apis/service';
import { BlockingService } from 'src/apis/service/BlockingService';
import { FollowService } from 'src/apis/service/FollowService';
import { MessagingService } from 'src/apis/service/MessagingService';
import { isDefinedAxiosRESTFailure } from 'src/apis/utils';
import GemLackModal from 'src/components/GemLackModal';
import { FOLLOW_QUERY_KEYS } from 'src/hooks/useFollowQuery';
import useLogin from 'src/hooks/useLogin';
import { showModalAtom } from 'src/stores/modal/atoms';
import { reportDMMessageThreadAPI } from 'src/stores/report/apis';
import { showCommonErrorToastAtom, showToastAtom } from 'src/stores/toast/atoms';
import { ModalType } from 'src/types/Modal';
import { MessageThreadReportRequest } from 'src/types/report';
import { ToastType } from 'src/types/Toast';
import { uploadContentManager } from 'src/utils/content-manager';
import { RESIZE_MAX_SIZE } from 'src/utils/imageUtils';
import { optimisticUpdaterFactory } from 'src/utils/query';
export const DM_QUERY_KEYS = {
  dmRoomList: () => ['messageThreads'],
  dmRoom: (messageThreadId: string) => [...DM_QUERY_KEYS.dmRoomList(), messageThreadId],
  dmRoomMessages: (messageThreadId: string) => [...DM_QUERY_KEYS.dmRoom(messageThreadId), 'messages'],
  imagePollStatus: (messageThreadId: string, messageUid: string) => ['pollSendImageResultV2', messageThreadId, messageUid]
};
export const useDMFriendsListQuery = () => {
  const {
    isLogin
  } = useLogin();
  return useQuery({
    queryKey: FOLLOW_QUERY_KEYS.dmLobbyFollowings(),
    async queryFn() {
      return new FollowService(new RESTClient()).getFollowingsWithFilter('ALL', {
        size: 6
      });
    },
    enabled: isLogin
  });
};
export const useDMRecentlyAddedFriendsCountQuery = () => {
  const {
    isLogin
  } = useLogin();
  return useQuery({
    queryKey: FOLLOW_QUERY_KEYS.dmLobbyFollowingCount(),
    async queryFn() {
      return new FollowService(new RESTClient()).getNewFollowerCount();
    },
    enabled: isLogin,
    select: ({
      count
    }) => count
  });
};
export const useDMRoomListQuery = (options?: {
  enabled?: boolean;
}) => {
  const {
    isLogin
  } = useLogin();
  const enabled = options?.enabled ?? true;
  return useQuery({
    queryKey: DM_QUERY_KEYS.dmRoomList(),
    async queryFn() {
      return new MessagingService(new JSONRPCClient()).listMessageThreads();
    },
    enabled: isLogin && enabled,
    refetchOnMount: true,
    select(data) {
      return data.filter(({
        friendInfo
      }) => friendInfo.friendType !== 'OFFICIAL').sort((a, b) => (b.dateLastMessageSent ?? 0) - (a.dateLastMessageSent ?? 0));
    }
  });
};
export const useDMRoomQuery = (messageThreadId: string) => {
  const {
    isLogin
  } = useLogin();
  return useQuery({
    queryKey: DM_QUERY_KEYS.dmRoom(messageThreadId),
    async queryFn() {
      return new MessagingService(new JSONRPCClient()).getMessageThread(messageThreadId);
    },
    enabled: isLogin && !!messageThreadId
  });
};
export interface MessageListPage {
  messages: DTOMessageInfo[];
  nextMessageSeqNo: number | null;
}
export const useDMRoomMessageListQuery = (messageThreadId: string, pageSize = 20) => {
  const [service] = useState(() => new MessagingService(new JSONRPCClient()));
  const {
    isLogin
  } = useLogin();
  return useInfiniteQuery<MessageListPage, unknown, InfiniteData<MessageListPage>, QueryKey, number | null>({
    queryKey: DM_QUERY_KEYS.dmRoomMessages(messageThreadId),
    async queryFn({
      pageParam
    }) {
      const prevMessageSeqNo = pageParam ?? undefined;
      const {
        messageInfos: messages,
        hasNext
      } = await service.getMessage({
        messageThreadId,
        direction: 'backward',
        prevMessageSeqNo,
        maxMessages: pageSize,
        noAck: true
      });
      if (!hasNext) {
        return {
          messages,
          nextMessageSeqNo: null
        };
      }
      const [{
        messageSeqNo
      }] = messages;
      const nextMessageSeqNo = messageSeqNo;
      return {
        messages,
        nextMessageSeqNo
      };
    },
    enabled: isLogin,
    initialPageParam: null,
    getNextPageParam: lastPage => lastPage.nextMessageSeqNo
  });
};
export const useUpdateDMQuery = (messageThreadId: string, readByUser = false) => {
  const queryClient = useQueryClient();
  return useCallback((messageInfo: DTOMessageInfo) => {
    queryClient.setQueryData<DTOMessageThreadInfo[]>(DM_QUERY_KEYS.dmRoomList(), optimisticUpdaterFactory<DTOMessageThreadInfo[]>(prev => {
      const index = prev.findIndex(messageThreadInfo => messageThreadInfo.messageThreadId === messageThreadId);
      if (index < 0) {
        return;
      }
      if (readByUser) {
        prev[index].messageSeqNoUserRead = messageInfo.messageSeqNo;
      }
      prev[index].lastMessage = messageInfo.messageContent;
      prev[index].lastMessageSeqNo = messageInfo.messageSeqNo;
      prev[index].lastMessageSentByMe = messageInfo.sentByMe;
      prev[index].dateLastMessageSent = messageInfo.dateMessageSent;
    }));
    queryClient.setQueryData<DTOMessageThreadInfo>(DM_QUERY_KEYS.dmRoom(messageThreadId), optimisticUpdaterFactory(prev => {
      if (readByUser) {
        prev.messageSeqNoUserRead = messageInfo.messageSeqNo;
      }
      prev.lastMessage = messageInfo.messageContent;
      prev.lastMessageSeqNo = messageInfo.messageSeqNo;
      prev.lastMessageSentByMe = messageInfo.sentByMe;
      prev.dateLastMessageSent = messageInfo.dateMessageSent;
    }));
  }, [queryClient, messageThreadId, readByUser]);
};
export const useAckMessageMutation = (messageThreadId: string) => {
  const {
    data: room
  } = useDMRoomQuery(messageThreadId);
  const queryClient = useQueryClient();
  return useMutation({
    async mutationFn(message: DTOMessageInfo) {
      if (!room) throw new Error('room not available');
      const {
        messageSeqNo
      } = message;
      const {
        messageSeqNoUserRead = 0
      } = room;
      if (messageSeqNo <= messageSeqNoUserRead) return;
      return new MessagingService(new JSONRPCClient()).ackMessage({
        messageThreadId,
        messageSeqNo
      });
    },
    onSuccess(_, {
      messageSeqNo
    }) {
      queryClient.setQueryData(DM_QUERY_KEYS.dmRoomList(), optimisticUpdaterFactory<DTOMessageThreadInfo[]>(prev => {
        const idx = prev.findIndex(messageThreadInfo => messageThreadInfo.messageThreadId === messageThreadId);
        if (idx < 0) return;
        prev[idx].messageSeqNoUserRead = messageSeqNo;
      }));
      queryClient.setQueryData(DM_QUERY_KEYS.dmRoom(messageThreadId), optimisticUpdaterFactory<DTOMessageThreadInfo>(prev => {
        prev.messageSeqNoUserRead = messageSeqNo;
      }));
    }
  });
};
export const useInsertTextMessageMutation = (messageThreadId: string, options?: Omit<MutationOptions<DTOMessageInfo, unknown, string, void>, 'mutationFn'>) => {
  const queryClient = useQueryClient();
  const updateDMQuery = useUpdateDMQuery(messageThreadId, true);
  return useMutation({
    async mutationFn(messageContent: string) {
      return new MessagingService(new JSONRPCClient()).sendTextMessage({
        messageThreadId,
        messageUid: '',
        messageContent,
        notTranslate: true
      });
    },
    ...options,
    async onSuccess(messageInfo, ...args) {
      updateDMQuery(messageInfo);
      queryClient.setQueryData<InfiniteData<MessageListPage>>(DM_QUERY_KEYS.dmRoomMessages(messageThreadId), optimisticUpdaterFactory(prev => {
        prev.pages[0].messages.push(messageInfo);
      }));
      await options?.onSuccess?.(messageInfo, ...args);
    }
  });
};
export const useInsertImageMessageMutation = (messageThreadId: string, options?: Omit<MutationOptions<void, unknown, File, unknown>, 'mutationFn'>) => {
  const [service] = useState(() => new MessagingService(new JSONRPCClient()));
  const queryClient = useQueryClient();
  const {
    data: {
      lastMessageSeqNo = 0
    } = {}
  } = useDMRoomQuery(messageThreadId);

  // const [, setProgress] = useImageUploadProgress(messageThreadId);

  const updateDMQuery = useUpdateDMQuery(messageThreadId, true);
  const [messageSeqNo, setMessageSeqNo] = useState(() => lastMessageSeqNo + 1);
  const pointNextMessage = () => setMessageSeqNo(prev => prev + 1);
  const messageUid = `image-${messageThreadId}-${messageSeqNo}`;
  const [isUploadCompleted, setUploadCompleted] = useState(true);
  useEffect(() => {
    if (lastMessageSeqNo) {
      setMessageSeqNo(lastMessageSeqNo + 1);
    }
  }, [lastMessageSeqNo]);
  const {
    data
  } = useQuery({
    queryKey: DM_QUERY_KEYS.imagePollStatus(messageThreadId, messageUid),
    async queryFn() {
      return service.pollSendImageResultV2(messageUid);
    },
    refetchInterval: 3000,
    enabled: !isUploadCompleted
  });
  useEffect(() => {
    if (!data) return;
    const {
      sent,
      messageInfo
    } = data;
    setUploadCompleted(sent);
    if (messageInfo) {
      pointNextMessage();
      // setProgress(messageInfo.messageSeqNo, 100);

      updateDMQuery(messageInfo);
      queryClient.setQueryData(DM_QUERY_KEYS.dmRoomMessages(messageThreadId), optimisticUpdaterFactory<InfiniteData<MessageListPage>>(prev => {
        const idx = prev.pages[0].messages.findIndex(msg => msg.messageSeqNo === messageSeqNo);
        if (idx >= 0) {
          prev.pages[0].messages[idx] = messageInfo;
        }
      }));
      queryClient.removeQueries({
        queryKey: DM_QUERY_KEYS.imagePollStatus(messageThreadId, messageUid)
      });
      window.requestAnimationFrame(() => {
        // setProgress(messageInfo.messageSeqNo, -1);
      });
    }
  }, [data, messageSeqNo, messageThreadId, queryClient, updateDMQuery, messageUid]);
  return useMutation<void, unknown, File, unknown>({
    ...options,
    onError(error, variables, context) {
      // NOTE: 에러 케이스에서 전송실패한 메세지가 채팅방에 보여지지 않도록 invalidateQueries
      // setQueryData를 사용하는 방법은 이미지 업로드 중 상대방이 보낸 메세지와 seqNo가 겹치는 이슈가 있어 추후 개선
      // invalidateQueries 또한 api 타이밍 이슈로 동시에 상대방이 보낸 메세지가 보여지지 않을 수 있는 이슈가 있음.
      queryClient.invalidateQueries({
        queryKey: DM_QUERY_KEYS.dmRoom(messageThreadId)
      });
      options?.onError?.(error, variables, context);
    },
    async mutationFn(file: File) {
      // setProgress(messageSeqNo, 0);
      const image = new Image();
      const messageInfo = await new Promise<DTOMessageInfo>((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const onLoad: EventListener = () => {
          const {
            width,
            height
          } = image;
          resolve({
            messageSeqNo,
            dateMessageSent: Date.now(),
            sentByMe: true,
            messageContent: DTO_MESSAGE_INFO_MESSAGE_CONTENT_TYPES.IMAGE,
            mediaInfo: {
              type: 'IMAGE_FULLSIZE',
              url,
              height,
              width
            }
          });
        };
        image.addEventListener('load', onLoad, {
          once: true
        });
        image.addEventListener('error', reject);
        image.src = url;
      });
      updateDMQuery(messageInfo);
      queryClient.setQueryData(DM_QUERY_KEYS.dmRoomMessages(messageThreadId), optimisticUpdaterFactory<InfiniteData<MessageListPage>>(prev => {
        prev.pages[0].messages.push(messageInfo);
      }));

      // setProgress(messageSeqNo, 33);

      const fileInfo = await uploadContentManager(file, {
        skipResize: true
      });

      // setProgress(messageSeqNo, 67);

      const {
        width,
        height
      } = image;
      const scale = Math.max(width / RESIZE_MAX_SIZE, height / RESIZE_MAX_SIZE);
      return service.sendImageMessage({
        fileInfo,
        messageThreadId,
        messageUid,
        width: width / scale,
        height: height / scale
      });
    },
    onSuccess(...args) {
      setUploadCompleted(false);
      options?.onSuccess?.(...args);
    }
  });
};
const PAYMENT_METHOD_TOAST_MESSAGE: Record<DTOOpenMessageThreadResponsePaymentMethodType, string> = {
  ITEM: 'message.toast_item_use',
  GEM: 'message.toast_gem_use'
};
export function useOpenDirectMessageThreadMutation({
  ...params
}: Omit<DTOOpenMessageThreadRequest, 'id'>, {
  onError,
  onSuccess
}: Pick<UseMutationOptions<string, unknown, string, unknown>, 'onError' | 'onSuccess'> = {}) {
  const queryClient = useQueryClient();
  const showCommonErrorToast = useSetAtom(showCommonErrorToastAtom);
  const showToast = useSetAtom(showToastAtom);
  const showModal = useSetAtom(showModalAtom);
  return useMutation<{
    messageThreadId: string;
    paymentMethod?: DTOOpenMessageThreadResponsePaymentMethodType;
  }, unknown, string, unknown>({
    async mutationFn(id) {
      const service = new MessagingService(new RESTClient());
      const {
        messageThreadId,
        paymentMethod
      } = await service.openMessageThread({
        id,
        ...params
      });
      return {
        messageThreadId,
        paymentMethod
      };
    },
    async onSuccess({
      messageThreadId,
      paymentMethod
    }, ...args) {
      await queryClient.invalidateQueries({
        queryKey: DM_QUERY_KEYS.dmRoomList(),
        refetchType: 'all'
      });
      await queryClient.invalidateQueries({
        queryKey: FOLLOW_QUERY_KEYS.follow(),
        refetchType: 'all'
      });
      await onSuccess?.(messageThreadId, ...args);
      if (paymentMethod) {
        showToast({
          type: ToastType.SUCCESS,
          message: PAYMENT_METHOD_TOAST_MESSAGE[paymentMethod]
        });
      }
    },
    onError(error, ...args) {
      if (isDefinedAxiosRESTFailure(error, REST_ERROR_CODES.NOT_ENOUGH_GEMS)) {
        const {
          wouldCostGems = 0
        } = error.response?.data.error.data ?? {};
        showModal({
          key: ModalType.GEM_LACK,
          component: () => <GemLackModal lackingGemAmount={wouldCostGems} gemLackTriggers={[]} />
        });
        return;
      } else {
        showCommonErrorToast();
      }
      onError?.(error, ...args);
    }
  });
}
export const useReportDMMessageThreadMutation = ({
  messageThreadId
}: {
  messageThreadId: string;
}, {
  onSuccess,
  ...options
}: UseMutationOptions<void, unknown, Omit<MessageThreadReportRequest, 'messageThreadId'>> = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    async mutationFn(request: Omit<MessageThreadReportRequest, 'messageThreadId'>) {
      return reportDMMessageThreadAPI({
        messageThreadId,
        ...request
      });
    },
    onSuccess(...args) {
      queryClient.invalidateQueries({
        queryKey: DM_QUERY_KEYS.dmRoomList(),
        refetchType: 'all'
      });
      queryClient.invalidateQueries({
        queryKey: FOLLOW_QUERY_KEYS.follow(),
        refetchType: 'all'
      });
      onSuccess?.(...args);
    },
    ...options
  });
};
export const useLeaveMessageThreadMutation = (options: UseMutationOptions<void, unknown, string, unknown> = {}) => {
  const queryClient = useQueryClient();
  const showCommonErrorToast = useSetAtom(showCommonErrorToastAtom);
  const {
    onSuccess,
    onError,
    ...restOptions
  } = options;
  return useMutation({
    async mutationFn(messageThreadId: string) {
      return new MessagingService(new JSONRPCClient()).closeMessageThread(messageThreadId);
    },
    async onSuccess(...args) {
      await queryClient.invalidateQueries({
        queryKey: DM_QUERY_KEYS.dmRoomList(),
        refetchType: 'all'
      });
      await onSuccess?.(...args);
    },
    onError(error, ...args) {
      showCommonErrorToast();
      onError?.(error, ...args);
    },
    ...restOptions
  });
};
export const useBlockAndLeaveMessageThreadMutation = (options: UseMutationOptions<void, unknown, {
  friendId: string;
}, unknown> = {}) => {
  const queryClient = useQueryClient();
  const showCommonErrorToast = useSetAtom(showCommonErrorToastAtom);
  const {
    onSuccess,
    onError,
    ...restOptions
  } = options;
  return useMutation({
    async mutationFn({
      friendId
    }: {
      friendId: string;
    }) {
      await new BlockingService(new JSONRPCClient()).blockFriend(friendId);
    },
    async onSuccess(result, ...args) {
      await queryClient.invalidateQueries({
        queryKey: FOLLOW_QUERY_KEYS.follow(),
        refetchType: 'all'
      });
      await queryClient.invalidateQueries({
        queryKey: DM_QUERY_KEYS.dmRoomList(),
        refetchType: 'all'
      });
      await onSuccess?.(result, ...args);
    },
    onError(error, ...args) {
      showCommonErrorToast();
      onError?.(error, ...args);
    },
    ...restOptions
  });
};